import { Text } from "@clipboard-health/ui-components";
import { Box, Button, Card, CardContent, CircularProgress, Stack, TextField } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useToast } from "@src/appV2/lib";
import { useState } from "react";

import type {
  CognitoAuthentication,
  CognitoUser,
} from "../../../../lib/cognito/src/lib/amplifyAuth";

interface EmailAuthTestProps {
  cognitoAuth: CognitoAuthentication;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  setResult: (result: string) => void;
  setApiResult: (result: string) => void;
  currentUser: CognitoUser | undefined;
  setCurrentUser: (user: CognitoUser | undefined) => void;
}

export function EmailAuthTest(props: EmailAuthTestProps) {
  const {
    cognitoAuth,
    isLoading,
    setIsLoading,
    setResult,
    setApiResult,
    currentUser,
    setCurrentUser,
  } = props;
  const [email, setEmail] = useState("");
  const [magicLink, setMagicLink] = useState("");
  const [linkRequested, setLinkRequested] = useState(false);

  const { showSuccessToast, showErrorToast } = useToast();

  const handleRequestMagicLink = async () => {
    if (!email) {
      showErrorToast("Please enter an email address");
      return;
    }

    setIsLoading(true);
    setResult("");
    setApiResult("");

    try {
      const response = await cognitoAuth.requestMagicLink(email);
      setLinkRequested(true);
      showSuccessToast(`Success! ${response.message}\n\nCheck your email for the magic link.`);
    } catch (error) {
      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? (error.message as string)
          : "An unknown error occurred";
      showErrorToast(`Error requesting magic link: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignInWithMagicLink = async () => {
    if (!magicLink) {
      showErrorToast("Please enter both email and magic link");
      return;
    }

    setIsLoading(true);

    try {
      const credentials = await cognitoAuth.signInWithEmailLink(magicLink);
      const userData = JSON.stringify(credentials.user, null, 2);
      setCurrentUser(credentials.user);
      setResult(`Sign in successful!\n\nUser: ${userData}`);
      setLinkRequested(false);
      setMagicLink("");
    } catch (error) {
      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? (error.message as string)
          : "An unknown error occurred";
      showErrorToast(`Error signing in: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelMagicLink = () => {
    setLinkRequested(false);
    setMagicLink("");
  };

  return (
    <Card>
      <CardContent>
        <Text
          variant="h6"
          sx={(theme: Theme) => ({
            marginBottom: theme.spacing(3),
          })}
        >
          Magic Link Authentication Test
        </Text>
        {!linkRequested && (
          <Stack
            sx={(theme: Theme) => ({
              gap: theme.spacing(2),
            })}
          >
            <TextField
              fullWidth
              label="Email Address"
              value={email}
              placeholder="Enter your email"
              type="email"
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
            <Button
              fullWidth
              variant="contained"
              disabled={isLoading || !email || Boolean(currentUser)}
              onClick={handleRequestMagicLink}
            >
              {isLoading ? <CircularProgress size={24} /> : "1. Request Magic Link"}
            </Button>
          </Stack>
        )}
        {linkRequested && (
          <Stack
            sx={(theme: Theme) => ({
              gap: theme.spacing(2),
            })}
          >
            <TextField
              fullWidth
              multiline
              label="Magic Link URL (paste from email)"
              value={magicLink}
              placeholder="Paste the magic link URL here"
              rows={3}
              onChange={(event) => {
                setMagicLink(event.target.value);
              }}
            />
            <Box
              sx={(theme: Theme) => ({
                display: "flex",
                gap: theme.spacing(1),
              })}
            >
              <Button
                fullWidth
                variant="contained"
                color="success"
                disabled={isLoading || !email || !magicLink || Boolean(currentUser)}
                onClick={handleSignInWithMagicLink}
              >
                {isLoading ? <CircularProgress size={24} /> : "Sign In"}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                disabled={isLoading}
                onClick={handleCancelMagicLink}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
